import { ArrowLeft, Lightbulb } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import {
  getStoredStudentPlanProjects,
  getStudentProjectById,
  pickStudentList,
  pickStudentText,
  saveStoredStudentPlanProjects,
} from '../student-projects.js'

const ROADMAP_TEXT = {
  zh: {
    addToPlan: '加入我的计划',
    back: '返回项目与简历',
    missingBody: '没有找到这个项目路线。请返回项目与简历重新选择一个项目。',
    missingTitle: '项目路线未找到',
    recommendedCycle: '推荐完成周期',
    removeFromPlan: '移出我的计划',
    roadmap: '项目路线',
    viewIdea: '查看项目思路',
  },
  en: {
    addToPlan: 'Add to my plan',
    back: 'Back to Student Zone',
    missingBody: 'We could not find this student project roadmap. Please return to the student zone and choose another project.',
    missingTitle: 'Project roadmap not found',
    recommendedCycle: 'Recommended cycle',
    removeFromPlan: 'Remove from my plan',
    roadmap: 'Learning roadmap',
    viewIdea: 'View project idea',
  },
}

function roadmapText(language) {
  return ROADMAP_TEXT[language] ?? ROADMAP_TEXT.zh
}

function StudentProjectRoadmapPage() {
  const navigate = useNavigate()
  const { language } = useLanguage()
  const { projectId } = useParams()
  const project = getStudentProjectById(projectId)
  const copy = roadmapText(language)
  const [planProjects, setPlanProjects] = useState(() => getStoredStudentPlanProjects())
  const added = project ? planProjects.some((item) => item.id === project.id) : false

  function toggleProjectPlan() {
    if (!project) {
      return
    }

    setPlanProjects((current) => {
      const exists = current.some((item) => item.id === project.id)
      const next = exists
        ? current.filter((item) => item.id !== project.id)
        : [
          ...current,
          {
            id: project.id,
            addedAt: new Date().toISOString(),
          },
        ]

      try {
        saveStoredStudentPlanProjects(next)
      } catch {
        return next
      }

      return next
    })
  }

  if (!project) {
    return (
      <div className="student-page page-container student-project-page">
        <button type="button" className="secondary-button student-page-back" onClick={() => navigate('/projects')}>
          <ArrowLeft size={17} />
          {copy.back}
        </button>
        <section className="student-project-empty">
          <span>{copy.roadmap}</span>
          <h1>{copy.missingTitle}</h1>
          <p>{copy.missingBody}</p>
          <button type="button" className="primary-button" onClick={() => navigate('/projects')}>
            {copy.back}
          </button>
        </section>
      </div>
    )
  }

  return (
    <div className="student-page page-container student-project-page">
      <div className="student-project-topbar">
        <button type="button" className="secondary-button student-page-back" onClick={() => navigate('/projects')}>
          <ArrowLeft size={17} />
          {copy.back}
        </button>
        <Link className="secondary-button" to={`/projects/${project.id}`}>
          <Lightbulb size={17} />
          {copy.viewIdea}
        </Link>
      </div>

      <section className="student-project-detail-hero">
        <span>{copy.roadmap}</span>
        <h1>{pickStudentText(project.title, language)}</h1>
        <p>{pickStudentText(project.overview, language)}</p>
        <div className="student-growth-summary">
          <span>{copy.recommendedCycle}: {pickStudentText(project.estimatedTime, language)}</span>
          {pickStudentList(project.goals, language).map((goal) => <span key={goal}>{goal}</span>)}
        </div>
      </section>

      <section className="student-roadmap-timeline">
        {pickStudentList(project.roadmap, language).map((stage, stageIndex) => (
          <article key={stage.stage}>
            <div className="student-roadmap-index">{stageIndex + 1}</div>
            <div>
              <h2>{stage.stage}</h2>
              <ul>
                {stage.tasks.map((task) => <li key={task}>{task}</li>)}
              </ul>
            </div>
          </article>
        ))}
      </section>

      <section className="student-project-bottom-actions">
        <button type="button" className="primary-button" onClick={toggleProjectPlan}>
          {added ? copy.removeFromPlan : copy.addToPlan}
        </button>
        <Link className="secondary-button" to={`/projects/${project.id}`}>
          {copy.viewIdea}
        </Link>
        <button type="button" className="secondary-button" onClick={() => navigate('/projects')}>
          {copy.back}
        </button>
      </section>
    </div>
  )
}

export default StudentProjectRoadmapPage
